import React from 'react';
import clsx from 'clsx';
import { TICK_LENGTH, TICK_MARGIN } from './utils';
import styles from './styles.css.js';
var LINE_HEIGHT = 16;
var prepareTicks = function (ticks, scale, tickFormatter) {
    var maxLines = 0;
    var lineLengths = [];
    var formattedTicks = ticks.map(function (value) {
        var x = scale.d3Scale(value);
        var label = tickFormatter ? tickFormatter(value) : value.toString();
        var labelLines = label.split('\n');
        maxLines = Math.max(maxLines, labelLines.length);
        lineLengths.push.apply(lineLengths, labelLines.map(function (line) { return line.length; }));
        return {
            x: x !== null && x !== void 0 ? x : NaN,
            label: label,
            labelLines: labelLines
        };
    });
    var averageLineLength = lineLengths.reduce(function (a, b) { return a + b; }, 0) / lineLengths.length || 0;
    return {
        maxLines: maxLines,
        averageLineLength: averageLineLength,
        formattedTicks: formattedTicks
    };
};
/**
 * Places the invisible left-hand side labels to calculate their maximum width.
 */
export function LeftHiddenLabels(_a) {
    var scale = _a.scale, ticks = _a.ticks, labelsRef = _a.labelsRef, tickFormatter = _a.tickFormatter;
    var labelMapper = function (value) {
        var y = scale.d3Scale(value);
        if (typeof y === 'undefined' || !isFinite(y)) {
            return null;
        }
        return (React.createElement("div", { key: "" + value, className: styles['labels-left__label'], "aria-hidden": "true" }, tickFormatter ? tickFormatter(value) : value.toString()));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: labelsRef, className: clsx(styles['labels-left'], styles['labels-left--hidden']) }, ticks.map(labelMapper))));
}
/** Renders the visible tick labels on the left axis, as well as their grid lines. */
export function LeftLabels(_a) {
    var ticks = _a.ticks, tickFormatter = _a.tickFormatter, title = _a.title, ariaRoleDescription = _a.ariaRoleDescription, scale = _a.scale, width = _a.width, horizontalBars = _a.horizontalBars;
    var offset = horizontalBars && scale.isCategorical() ? Math.max(0, scale.d3Scale.bandwidth() - 1) / 2 : 0;
    return (React.createElement("g", { className: clsx(styles['labels-left']), "aria-label": title, role: "list", "aria-roledescription": ariaRoleDescription }, ticks.map(function (value, i) {
        var _a;
        var _b;
        var y = (_b = scale.d3Scale(value)) !== null && _b !== void 0 ? _b : NaN;
        return (isFinite(y) && (React.createElement("g", { key: "left-tick-" + i, role: "listitem", transform: "translate(0," + (y + offset) + ")", className: clsx(styles.ticks, (_a = {},
                _a[styles['ticks--x']] = !!horizontalBars,
                _a[styles['ticks--y']] = !horizontalBars,
                _a)) },
            !horizontalBars && (React.createElement("line", { className: styles.grid, x1: -TICK_LENGTH, y1: 0, x2: width, y2: 0, "aria-hidden": "true" })),
            React.createElement("text", { x: -(TICK_LENGTH + TICK_MARGIN), y: 0 }, tickFormatter ? tickFormatter(value) : value.toString()))));
    })));
}
/** Renders the visible tick labels on the bottom axis, as well as their grid lines. */
export function BottomLabels(_a) {
    var ticks = _a.ticks, tickFormatter = _a.tickFormatter, scale = _a.scale, width = _a.width, title = _a.title, ariaRoleDescription = _a.ariaRoleDescription, horizontalBars = _a.horizontalBars;
    if (!ticks.length) {
        return null;
    }
    var _b = prepareTicks(ticks, scale, tickFormatter), formattedTicks = _b.formattedTicks, maxLines = _b.maxLines, averageLineLength = _b.averageLineLength;
    var height = TICK_LENGTH + TICK_MARGIN + maxLines * LINE_HEIGHT;
    var xOffset = scale.isCategorical() && !horizontalBars ? Math.max(0, scale.d3Scale.bandwidth() - 1) / 2 : 0;
    var hasBottomCategories = scale.isCategorical() && !horizontalBars;
    var reduceLabelRatio = 1;
    if (hasBottomCategories) {
        // If we are displaying categories on the bottom axis, we show all domain values by default.
        // However, this can very quickly result in overlapping text, so we are using a heuristic
        // to hide labels when necessary:
        // 1) calculate the average width of each tick label line (averageLabelWidth)
        // 2) find out how many of these we would be able to fit currently (targetCount)
        // 3) if necessary, calculate the (rounded-up) ratio of needed width to actual width
        // We are not using a monospace font, but 12px is a decent average for this heuristic.
        var CHAR_WIDTH = 12;
        var averageLabelWidth = CHAR_WIDTH * averageLineLength;
        var targetCount = Math.ceil(width / averageLabelWidth);
        if (ticks.length > targetCount) {
            reduceLabelRatio = Math.max(1, Math.ceil((averageLabelWidth * ticks.length) / width));
        }
    }
    return (React.createElement("svg", { width: width, height: height, className: styles['labels-bottom'], focusable: false },
        React.createElement("g", { "aria-label": title, role: "list", "aria-roledescription": ariaRoleDescription }, formattedTicks.map(function (_a, i) {
            var _b;
            var x = _a.x, label = _a.label, labelLines = _a.labelLines;
            var isHidden = reduceLabelRatio > 1 && i % reduceLabelRatio !== 0;
            return (React.createElement("g", { key: "bottom-tick-" + i, transform: "translate(" + (x + xOffset) + ",0)", className: clsx(styles.ticks, styles['ticks--x'], styles['ticks--bottom'], (_b = {},
                    _b[styles['ticks--hidden']] = isHidden,
                    _b)), role: "listitem", "aria-label": label },
                React.createElement("line", { x1: 0, x2: 0, y1: 0, y2: TICK_LENGTH, "aria-hidden": "true" }),
                isHidden ? (React.createElement("text", { key: "line-" + i, x: 0, y: TICK_LENGTH + TICK_MARGIN }, label)) : (labelLines.map(function (line, i) { return (React.createElement("text", { key: "line-" + i, x: 0, y: TICK_LENGTH + TICK_MARGIN + i * LINE_HEIGHT }, line)); }))));
        }))));
}
//# sourceMappingURL=labels.js.map